/**
 * Turning a roof template plus a set of measurements into real orders.
 *
 * Given the products on a template and the roof they are going on, work out how
 * many of each are needed and which document each one belongs to. Pure
 * arithmetic, kept here so it is testable and so the MCP server can offer the
 * same operation.
 *
 * The quantity rule is ADR 0021, using each product's own figures rather than
 * anything typed on the template:
 *
 *     qty = ceil(measurement x (1 + waste/100) / coverage)
 *
 * A product with no coverage cannot be worked out that way and comes back as a
 * skip rather than a guess — a silent quantity of 1 against a roof's worth of
 * shingles is how a job loses money quietly.
 *
 * A template line may also carry its own quantity, which beats all of the
 * above. Some counts do not follow the roof: two slant back vents and two of
 * each flashing go on every replacement, big or small.
 */

import { flatSquaresOf } from "./flatRoof.js";
import {
  WALL_MEASUREMENT_KEYS,
  wallMeasurement,
  type WallMeasurements,
} from "./siding.js";
import { toCents, type Cents } from "./money.js";
import { RIDGE_VENT_SHARE_OF_RIDGE } from "./roofReport.js";
import { NO_PRICES, PriceBook } from "./supplierPricing.js";
import {
  INSTALL_STEP_SPAN,
  roofSlot,
  shingleInstallRank,
  targetForCategory,
  workOrderRank,
  type LineTarget,
  type RoofSlot,
} from "./roofSlots.js";

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The measurements a template draws on — a row of `job_measurements`, or, for
 * a siding template, a row of `job_wall_measurements` (see siding.ts). One
 * shape, because the slot resolver is handed whichever surface the template
 * is measured on and reads the keys it knows.
 */
export interface RoofMeasurements extends WallMeasurements {
  squares?: unknown;
  eave_lf?: unknown;
  rake_lf?: unknown;
  ridge_lf?: unknown;
  ridge_vent_lf?: unknown;
  hip_lf?: unknown;
  gutter_lf?: unknown;
  valley_lf?: unknown;
  flashing_lf?: unknown;
  step_flashing_lf?: unknown;
  /** Roof-to-roof transitions, where one plane meets another. */
  transition_lf?: unknown;
  flat_squares?: unknown;
  /** Parapet wall, in feet, and how tall it is. See `wallSquaresOf`. */
  wall_length_lf?: unknown;
  wall_height_ft?: unknown;
  /**
   * Whether the insulation goes up the walls too — the rep's answer, not a
   * measurement. It rides on the roof because that is what the slot resolver
   * is handed, and it changes which area the insulation is bought against.
   */
  wall_insulation?: boolean;
  flat_perimeter_lf?: unknown;
  waste_pct?: unknown;
  /**
   * The two steep-charge bands, in squares. What the measurement form asks
   * for, and what the steep slots price off.
   */
  steep_mid_squares?: unknown;
  steep_high_squares?: unknown;
  /**
   * The per-pitch breakdown. Read from measurement reports and asked for by
   * hand until 2026-08-13, when the form collapsed to the two bands above.
   * Still carried so that every roof measured before that prices the same.
   */
  pitch_7_12?: unknown;
  pitch_8_12?: unknown;
  pitch_9_12?: unknown;
  pitch_10_12?: unknown;
  pitch_11_12?: unknown;
  pitch_12_12?: unknown;
  pitch_13_12?: unknown;
  pitch_14_12?: unknown;
  pitch_15_12?: unknown;
  pitch_16_12?: unknown;
  /** Layers of roof coming off. One unless somebody says otherwise. */
  layers?: unknown;
}

/**
 * Every roof on the job.
 *
 * A job is rarely one roof. A house with a detached garage is two layouts, and
 * both of them need shingles. Anything that reads a measurement takes either
 * form so that a single roof still behaves exactly as it always did.
 */
export type RoofLayouts = RoofMeasurements | readonly RoofMeasurements[];

/** The layouts as a list, however they arrived. */
export function asLayouts(m: RoofLayouts): readonly RoofMeasurements[] {
  return Array.isArray(m)
    ? (m as readonly RoofMeasurements[])
    : [m as RoofMeasurements];
}

/**
 * How many squares of roof have to come off, layers and all.
 *
 * Layers are counted per roof, never pooled: a two-layer house beside a
 * one-layer garage tears off `house x 2 + garage`, not `(house + garage) x 3`.
 */
export function tearoffSquares(m: RoofLayouts): number {
  return asLayouts(m).reduce((total, layout) => {
    const layers = Math.max(1, Math.round(num(layout.layers ?? 1)));
    return total + (num(layout.squares) + num(layout.flat_squares)) * layers;
  }, 0);
}

/**
 * Squares of roof under the top layer: every layer after the first.
 *
 * The base labour already pays the crew to strip one layer. Each layer below
 * it is more work on the same roof, and Jonathon, 2026-10-15: "it's $10 more
 * per square per layer. don't count waste". Counted per roof like
 * `tearoffSquares`, and zero on a one-layer roof or a new build with none.
 */
export function extraLayerSquares(m: RoofLayouts): number {
  return asLayouts(m).reduce((total, layout) => {
    const layers = Math.round(num(layout.layers ?? 1));
    const extra = Math.max(0, layers - 1);
    return total + (num(layout.squares) + num(layout.flat_squares)) * extra;
  }, 0);
}

/**
 * Resolve what a slot measures.
 *
 * Three of them are worked out rather than read: the steep bands add up the
 * per-pitch squares, and the tear-off figure multiplies the roof by its layers.
 * None of them include waste — the caller applies that, the same as everywhere
 * else, so a roof's waste allowance reaches labour and disposal too.
 */
export function resolveMeasurement(key: string, m: RoofLayouts): number {
  return asLayouts(m).reduce(
    (total, layout) => total + onOneRoof(key, layout),
    0,
  );
}

/** The two steep bands: the stored band when there is one, else the per-pitch sum. */
function steepBand(key: string, m: RoofMeasurements): number | null {
  if (key === "steep_mid_squares") {
    if (m.steep_mid_squares != null) return num(m.steep_mid_squares);
    return (
      num(m.pitch_8_12) +
      num(m.pitch_9_12) +
      num(m.pitch_10_12) +
      num(m.pitch_11_12) +
      num(m.pitch_12_12)
    );
  }
  if (key === "steep_high_squares") {
    if (m.steep_high_squares != null) return num(m.steep_high_squares);
    return (
      num(m.pitch_13_12) +
      num(m.pitch_14_12) +
      num(m.pitch_15_12) +
      num(m.pitch_16_12)
    );
  }
  return null;
}

function onOneRoof(key: string, m: RoofMeasurements): number {
  /*
   * The band is a column now, and the per-pitch sum is the fallback.
   *
   * Both have to work at once and will for as long as the company has roofs it
   * measured before 2026-08-13: those rows carry ten per-pitch figures and no
   * band, and re-pricing one of them has to produce the number it produced the
   * first time. A stored band wins when there is one, because that is what the
   * rep typed.
   */
  const steep = steepBand(key, m);
  if (steep !== null) return steep;
  if (key === "hip_ridge_lf") return num(m.ridge_lf) + num(m.hip_lf);
  // Ridge vent is 80% of the ridge (Jonathon, 2026-09-30, ADR 0239). The measurement
  // form derives it when a report is read, but a layout keyed in by hand — or
  // measured before that existed — leaves it blank, and a blank one is not
  // "no ridge vent": it silently dropped the Cobra line off every material
  // order, on templates that had carried it all along.
  if (key === "ridge_vent_lf") {
    const measured = num(m.ridge_vent_lf);
    if (measured > 0) return measured;
    return num(m.ridge_lf) * RIDGE_VENT_SHARE_OF_RIDGE;
  }
  if (key === "tearoff_squares") return tearoffSquares(m);
  if (key === "extra_layer_squares") return extraLayerSquares(m);

  /*
   * The three flat-roof areas, which are three different numbers.
   *
   * A parapet wall is roof as far as the membrane is concerned — it is
   * covered, and the crew is paid for covering it — but it is not roof to the
   * insulation unless somebody says so, and it is never roof to the flute
   * fill, which fills the ribs of a deck a parapet does not have. Jonathon,
   * 2026-08-31, in that order: "yes it does", "just have insulation stick to
   * the roof", "it's based on just the roof area. doesn't go on the walls".
   *
   * One base per rule, so a template line says which one it means rather than
   * every line sharing a figure that is right for one of them.
   */
  /*
   * These three read `flatSquaresOf` rather than `flat_squares` itself: a
   * building that is entirely flat is measured into `squares`, because that is
   * where a measurement report puts "total roof area". See the note there —
   * job 1060 is what reading the column alone cost.
   *
   * The older `flat_field` slot above is deliberately left alone. It is the
   * low-slope patch on a pitched roof — every shingle template carries four
   * lines of roll roofing against it — and falling back to the roof area there
   * would order roll roofing for an entire shingle house.
   */
  if (key === "flat_membrane_squares")
    return flatSquaresOf(m) + wallSquaresOf(m);
  if (key === "flat_deck_squares") return flatSquaresOf(m);
  if (key === "flat_insulation_squares")
    return (
      flatSquaresOf(m) + (m.wall_insulation === true ? wallSquaresOf(m) : 0)
    );

  /*
   * The open edge, and the wall, which between them are the perimeter.
   *
   * "coping goes on walls. drip edge and termination bar goes on eaves and
   * rakes. you don't use drip edge or termination bar where coping would go
   * since the coping serves that purpose." Two runs that must not overlap, so
   * they are two measurements rather than one perimeter shared out.
   */
  if (key === "open_edge_lf") return num(m.eave_lf) + num(m.rake_lf);
  if (key === "wall_lf") return num(m.wall_length_lf);

  // The wall, for a siding template. siding.ts owns the arithmetic.
  if (WALL_MEASUREMENT_KEYS.includes(key)) return wallMeasurement(key, m);

  return num((m as Record<string, unknown>)[key]);
}

/** Squares of parapet: how tall it is, times how far it runs. */
function wallSquaresOf(m: RoofMeasurements): number {
  const length = num(m.wall_length_lf);
  const height = num(m.wall_height_ft);
  if (length <= 0 || height <= 0) return 0;
  return (length * height) / 100;
}

/** A product as the generator needs it. */
export interface GeneratorProduct {
  id: string;
  name: string | null;
  /** The words a copy line carries onto the estimate. See `section`. */
  description?: string | null;
  category: string | null;
  unit: string | null;
  default_cost: unknown;
  /** How much one unit covers. The unit is set by `measurement` — see below. */
  coverage: unknown;
  /** The product's own waste allowance, when it has one. */
  waste: unknown;
  /**
   * Roofr's measurement key, which says what `coverage` is measured in.
   *
   * This matters more than it looks. A bundle of Timberline HDZ has a coverage
   * of 33.33 against TOTAL_PITCH_AREA — thirty-three square FEET — while a
   * product keyed to TOTAL_PITCH_AREA_SQUARES is counted per square. Feeding
   * squares to the first would order 2 bundles for a roof that needs 104.
   */
  measurement?: string | null | undefined;
  /** "material", "labor" or "other". Decides which document the line goes on. */
  kind?: string | null;
  /** The framing nailer this nail fits, when it is one. See framingNails.ts. */
  framing_nailer?: string | null;
}

/** Measurement keys whose coverage is per square foot, not per square. */
const SQUARE_FOOT_KEYS = ["TOTAL_PITCH_AREA", "TOTAL_FLAT_AREA"];

/**
 * Slot measurements that are an area in squares rather than a length in feet.
 *
 * One list, because two places ask — the quantity and the unit shown beside
 * it — and a key missing from either is silently wrong rather than broken. A
 * board of ISO covers 32 square feet; read against a slot this does not
 * recognise as an area, its coverage is treated as 32 *squares* and the order
 * comes out at a hundredth of the roof.
 */
const AREA_MEASUREMENTS = [
  "squares",
  "flat_squares",
  "flat_membrane_squares",
  "flat_insulation_squares",
  "flat_deck_squares",
  "steep_mid_squares",
  "steep_high_squares",
  "tearoff_squares",
  "extra_layer_squares",
  "siding_squares",
  "siding_removal_squares",
  "house_wrap_squares",
  "soffit_squares",
];

/**
 * Slot measurements that are a count of things rather than an area or a
 * length: posts per corner, windows to wrap. Their coverage is "per one".
 */
const COUNT_MEASUREMENTS = [
  "outside_corner_posts",
  "inside_corner_posts",
  "openings_ea",
];

const isCountMeasurement = (key: string | null | undefined): boolean =>
  COUNT_MEASUREMENTS.includes((key ?? "").trim());

const isAreaMeasurement = (key: string | null | undefined): boolean =>
  AREA_MEASUREMENTS.includes((key ?? "").trim());

/** A square is 100 square feet. */
const SQUARE_FEET = 100;

/**
 * The measurement to feed a product, in the units its coverage expects.
 *
 * Areas are held in squares; a product priced per square foot needs them
 * multiplied out first. Lengths are feet on both sides and pass straight
 * through.
 */
export function measurementForProduct(
  slotMeasurement: string,
  value: number,
  productMeasurement: string | null | undefined,
): number {
  if (!isAreaMeasurement(slotMeasurement)) return value;

  const keys = (productMeasurement ?? "")
    .split(",")
    .map((k) => k.trim().toUpperCase());
  // Only convert when the product asks for the raw area and not the squares
  // variant — several products list both, and squares wins.
  const wantsSquares = keys.some((k) => k.endsWith("_SQUARES"));
  const wantsFeet = keys.some((k) => SQUARE_FOOT_KEYS.includes(k));
  return !wantsSquares && wantsFeet ? value * SQUARE_FEET : value;
}

/**
 * What a product's coverage is measured in, so a person can read it.
 *
 * Follows exactly the same reasoning as `measurementForProduct` — a bundle of
 * Timberline HDZ covers 33.33 square feet, a roll of felt covers 10 squares,
 * and a stick of drip edge covers 10 feet. Writing "33.33 SQ" on the screen
 * would be a lie that reads perfectly plausibly.
 */
export function coverageUnit(
  slotMeasurement: string | null,
  productMeasurement: string | null | undefined,
): "SQ" | "SF" | "LF" | "EA" {
  if (slotMeasurement === null) return "SQ";
  if (isCountMeasurement(slotMeasurement)) return "EA";
  if (!isAreaMeasurement(slotMeasurement)) return "LF";

  const keys = (productMeasurement ?? "")
    .split(",")
    .map((k) => k.trim().toUpperCase());
  const wantsSquares = keys.some((k) => k.endsWith("_SQUARES"));
  const wantsFeet = keys.some((k) => SQUARE_FOOT_KEYS.includes(k));
  return !wantsSquares && wantsFeet ? "SF" : "SQ";
}

/** One product on a template, in one section. */
export interface RoofTemplateItem {
  id: string;
  slot: string;
  product_id: string | null;
  /**
   * A quantity written down rather than worked out. Two slant back vents and
   * two pieces of each flashing go on every replacement whatever it measures,
   * and nobody wants to key that in each time. Null means work it out.
   */
  qty?: unknown;
  /**
   * What to order when this section has no measurement on the roof.
   *
   * Different from `qty`, which overrides the measurement. Two pieces of
   * transition metal is a sensible guess on a roof nobody has measured
   * transitions on; the moment somebody does measure them, the measurement
   * wins.
   */
  default_qty?: unknown;
  /**
   * Buy this line from a named supplier rather than the one the estimate was
   * generated against. Furring strips come from Home Depot while the panels
   * come from Central States, so one job raises two orders.
   */
  supplier_id?: string | null;
  /**
   * The proposal heading this line sits under — "What You Get", "Warranty".
   *
   * A line with one is **wording, not work**: it is the paragraph the customer
   * reads about this package, and it belongs on the estimate rather than on a
   * material order. `generatePlan` sets those aside as `copyItems` and never
   * orders them; nobody delivers a warranty paragraph to site.
   */
  section?: string | null;
  /**
   * How much one unit covers *in this section*, when that differs from the
   * product's own figure.
   *
   * A roll of butyl tape is forty-five feet of tape however it is used. At the
   * rake it runs one bead, so it covers forty-five feet of rake. At the ridge
   * it runs a bead down each side, so the same roll covers twenty-two and a
   * half. Coverage lives on the product, so without this the template has to
   * choose one rate and be wrong at the other end — either short at the ridge
   * or double at the rake.
   *
   * Null means use the product's own coverage, which is almost always right.
   */
  coverage?: unknown;
  /**
   * Which build this line belongs to, on a flat roof — `iso:adhered`,
   * `termination:kynar`, `coping`. Null means every build gets it.
   *
   * Read by `lineApplies` in flatRoof.ts before generation, not here: this
   * function turns lines into quantities, and which lines exist is a decision
   * taken before it is called.
   */
  applies_when?: string | null;
  /**
   * Which girth answer sizes this line, when no coverage can — 'termination'
   * or 'coping'. See `girthQuantity` in flatRoof.ts. The builder resolves it
   * into `qty` before generating, for the same reason.
   */
  girth_source?: string | null;
  /**
   * Which counted answer sizes this line, on a siding template — a vent, a
   * block, a dryer vent the rep counts before generating. See
   * `withSidingCounts` in siding.ts; the builder resolves it into `qty`.
   * A deck template's lines name theirs the same way (deck.ts).
   */
  count_source?: string | null;
  /**
   * How a counted quantity was worked out — "2 row(s) × 3 posts" — when a
   * rule rather than a measurement produced it. Never stored: `withDeckCounts`
   * writes it beside the `qty` it resolves, and it becomes the calc note in
   * place of "set count", so whoever checks the order can check the rule.
   */
  count_note?: string | null;
  sort_order: number;
}

export interface GeneratedLine {
  target: LineTarget;
  product_id: string;
  description: string;
  unit: string | null;
  qty: number;
  unitPrice: Cents;
  sort_order: number;
  /** Null means the supplier chosen when the estimate was generated. */
  supplierId: string | null;
  /**
   * The measurement this quantity was priced off — "Valley = 30 LF". Merged
   * lines carry both, so drip edge shows the eave and the rake. Internal: it
   * is not on the copy that goes to the crew or the supplier.
   */
  calc: string;
}

/** One paragraph of the package's wording, and the heading it sits under. */
export interface GeneratedCopy {
  product_id: string;
  /** The product's name, which is what the estimate line is called. */
  description: string;
  /** The words themselves, from the product's description. */
  note: string | null;
  section: string;
  sort_order: number;
}

export interface GeneratedPlan {
  materialItems: GeneratedLine[];
  workItems: GeneratedLine[];
  /** The package wording, for the estimate. Never ordered. */
  copyItems: GeneratedCopy[];
  /** Lines that could not be worked out, and why. Shown, never swallowed. */
  skipped: { name: string; reason: string }[];
}

/** Numbers as a person would write them: 34.5, not 34.500000000000004. */
const trim = (n: number): string =>
  Number(n.toFixed(2)).toLocaleString("en-US");

/**
 * The measurement this line was priced off — "Valley = 30 LF".
 *
 * Not the arithmetic. Somebody reviewing an order wants to know which number
 * off the report drove the quantity, so they can check that number; the
 * division that follows is the machine's business.
 */
/** A slot's name as a person says it: "Valley", not "Valley products". */
const section = (slot: RoofSlot): string =>
  slot.label.replace(/ products$/, "");

/**
 * What a line was worked out from, as the crew reads it.
 *
 * The waste is spelled out rather than left implicit. A steep charge reading
 * "8/12 - 12/12 = 42 SQ" beside a quantity of 48.3 looks like an arithmetic
 * error to anyone checking the work order, and a crew that cannot check the
 * number is a crew that either queries every line or trusts all of them.
 * Jonathon, 2026-08-06: "work order needs to reflect squares with waste on the
 * pitch charge."
 *
 * A charge by the running foot carries no waste, so nothing is added to it.
 */
function calcNote(slot: RoofSlot, measured: number, waste: number): string {
  const unit = coverageUnit(slot.measurement, null);
  const base = `${section(slot)} = ${trim(measured)} ${unit}`;
  if (waste <= 0) return base;
  const withWaste = measured * (1 + waste / 100);
  return `${base} + ${trim(waste)}% waste = ${trim(
    Math.round(withWaste * 100) / 100,
  )} ${unit}`;
}

/**
 * `qty = ceil(measurement x (1 + waste/100) / coverage)`.
 *
 * Rounding up is what makes it a materials figure: you cannot buy two thirds of
 * a bundle, so a roof needing 3.1 sticks of drip edge buys four. Pass `false`
 * for `round` where the quantity is not a thing you buy — see below.
 */
export function quantityFor(
  measurement: number,
  coverage: unknown,
  waste: unknown,
  round = true,
): number | null {
  const cover = num(coverage);
  if (cover <= 0) return null;
  if (measurement <= 0) return 0;
  const exact = (measurement * (1 + num(waste) / 100)) / cover;
  // Two decimals so a crew reads 34.5 squares rather than 34.499999999999996.
  return round ? Math.ceil(exact) : Math.round(exact * 100) / 100;
}

/**
 * Whether a line's quantity is a count of things or a measurement.
 *
 * A crew paid by the square is paid for 34.5 squares, not 35 — the roof does
 * not grow to the next whole square, and rounding every labour line up is a
 * few hundred dollars given away across a job. But a dump trailer is a thing
 * you either have or do not, and half a chimney reflash is not a job. So it is
 * the unit that decides: squares and feet are measured, everything else is
 * counted.
 */
const MEASURED_UNITS: readonly string[] = ["SQ", "LF", "SF"];

export function isMeasuredLabour(
  target: LineTarget,
  unit: string | null | undefined,
): boolean {
  return (
    target === "work_order" &&
    MEASURED_UNITS.includes((unit ?? "").trim().toUpperCase())
  );
}

/**
 * Build the material and work order lines for a template on a given roof.
 *
 * The roof's own waste allowance beats the product's when the layout carries
 * one: a rep who put 15% on the measurement meant it for the whole roof.
 */
export function generatePlan(
  items: readonly RoofTemplateItem[],
  measurements: RoofLayouts,
  products: readonly GeneratorProduct[],
  /**
   * Prices, when the caller has them. A line bought from a named supplier is
   * priced off that supplier's sheet; everything else falls back to the
   * product's own cost, which is what happened before this existed.
   */
  prices: PriceBook = NO_PRICES,
  /** The supplier the order as a whole is going to, when a line names none. */
  defaultSupplierId: string | null = null,
  /**
   * Sequence the material order the way the roof goes on, rather than the way
   * the template lists it. Shingle only — metal roofs carry their own sequence
   * per template line and must not be re-sorted (see roofSlots.ts).
   */
  options: { installOrder?: "shingle" } = {},
): GeneratedPlan {
  const byId = new Map(products.map((p) => [p.id, p]));
  const materialItems: GeneratedLine[] = [];
  const workItems: GeneratedLine[] = [];
  const copyItems: GeneratedCopy[] = [];
  const skipped: { name: string; reason: string }[] = [];
  // Waste is an allowance, not a quantity, so it cannot be added up. Where two
  // roofs disagree the larger one wins: ordering short is the expensive mistake.
  const wastes = asLayouts(measurements)
    .filter((layout) => layout.waste_pct != null)
    .map((layout) => num(layout.waste_pct));
  const layoutWaste = wastes.length === 0 ? null : Math.max(...wastes);

  const ordered = [...items].sort((a, b) => a.sort_order - b.sort_order);

  for (const item of ordered) {
    const slot = roofSlot(item.slot);
    const product = item.product_id ? byId.get(item.product_id) : undefined;

    /*
     * Wording rather than work. It carries no quantity, no price and no
     * measurement — it is a paragraph — so it never reaches the slot logic
     * below, which exists to work out how much of something to order.
     */
    // Not `section`: that is the module's own helper for naming a roof slot,
    // and shadowing it here breaks every measurement message below.
    const copySection = (item.section ?? "").trim();
    if (copySection !== "") {
      if (!product) {
        skipped.push({ name: item.id, reason: "the product has been removed" });
        continue;
      }
      copyItems.push({
        product_id: product.id,
        description: product.name ?? "",
        note: (product.description ?? "").trim() || null,
        section: copySection,
        sort_order: item.sort_order,
      });
      continue;
    }

    if (!slot) {
      skipped.push({
        name: item.product_id ?? item.id,
        reason: `unknown section "${item.slot}"`,
      });
      continue;
    }
    if (!product) {
      skipped.push({ name: item.id, reason: "the product has been removed" });
      continue;
    }

    const fixed = item.qty == null ? null : num(item.qty);
    const target = targetForCategory(product.category, product.kind);
    const measuredLabour = isMeasuredLabour(target, product.unit);
    const byTheFoot =
      measuredLabour && (product.unit ?? "").trim().toUpperCase() === "LF";

    let qty: number | null;
    let calc: string;
    if (fixed !== null) {
      // Written down on the template. It beats the measurement either way —
      // a line that says "two" means two.
      qty = fixed;
      // No measurement drove it, so there is nothing to check it against —
      // unless a rule worked it out, in which case the rule is the check.
      calc = (item.count_note ?? "").trim() || "set count";
    } else if (slot.measurement === null) {
      // An "ea" section with nothing written down: one per job.
      qty = 1;
      calc = "set count";
    } else {
      const measured = resolveMeasurement(slot.measurement, measurements);
      if (measured <= 0) {
        // Nothing measured for this part of the roof is not an error — a roof
        // with no valleys needs no valley metal. A line may name a fallback
        // though, for the parts nobody measures every time.
        const fallback = item.default_qty == null ? 0 : num(item.default_qty);
        if (fallback <= 0) continue;
        qty = fallback;
        calc = `${trim(fallback)} assumed — no ${section(
          slot,
        ).toLowerCase()} measured`;
      } else {
        // Waste is an allowance on area — you buy 15% more roof than you
        // cover. A charge by the running foot has none of that in it: forty
        // feet of ridge is forty feet of ridge to cut and vent, and thirty
        // feet of wall is thirty feet to reflash.
        // Nor on a section that says so: a post per corner, a wrap per
        // window, the siding that is already on the wall. See `noWaste`.
        const waste =
          byTheFoot || slot.noWaste ? 0 : num(layoutWaste ?? product.waste);
        const forProduct = measurementForProduct(
          slot.measurement,
          measured,
          product.measurement,
        );
        // The section may state its own coverage — see `coverage` on the item.
        const covers = item.coverage == null ? product.coverage : item.coverage;
        qty = quantityFor(forProduct, covers, waste, !measuredLabour);
        if (qty === null) {
          skipped.push({
            name: product.name ?? product.id,
            reason: "no coverage on the product, so the quantity is unknown",
          });
          continue;
        }
        calc = calcNote(slot, measured, waste);
      }
    }

    if (qty <= 0) continue;

    const supplierId = item.supplier_id ?? defaultSupplierId;
    const line: GeneratedLine = {
      target,
      product_id: product.id,
      description: [product.category, product.name].filter(Boolean).join(" | "),
      unit: product.unit,
      qty,
      // Labour is not bought from a supplier, so it keeps the catalogue rate
      // whichever supplier the materials happen to be coming from.
      unitPrice:
        target === "work_order"
          ? toCents(product.default_cost)
          : prices.costOf(product, supplierId),
      sort_order: item.sort_order,
      // Still only set when the line names its own supplier — the order's own
      // supplier is not a per-line override and must not read like one.
      supplierId: item.supplier_id ?? null,
      calc,
    };

    if (
      options.installOrder === "shingle" &&
      line.target === "material_order"
    ) {
      // The order is read against the roof, so it runs in the order the roof is
      // built. Only the position moves — quantity, price and name are untouched.
      //
      // A step can hold more than one product — two shingles, two flashings —
      // and the rank alone would give them the same sort_order and leave the
      // order within the step to whatever the database happened to return.
      // Multiplying up leaves room for the template's own order to break the
      // tie, so the same roof reads the same way every time it is opened.
      line.sort_order =
        shingleInstallRank(product.name, product.category) * INSTALL_STEP_SPAN +
        materialItems.length;
    }

    if (line.target === "work_order") {
      // The work order reads in the order a roof goes on, not the order the
      // template happens to list. See `workOrderRank`. Multiplied up so the
      // template's own order breaks a tie between two lines in the same step,
      // the same way the material sequence does.
      workItems.push({
        ...line,
        sort_order:
          workOrderRank(slot.key, product.name) * INSTALL_STEP_SPAN +
          workItems.length,
      });
    } else {
      materialItems.push(line);
    }
  }

  return {
    materialItems: mergeLines(materialItems),
    workItems: mergeLines(workItems),
    copyItems: copyItems.sort((a, b) => a.sort_order - b.sort_order),
    skipped,
  };
}

/**
 * One line per product.
 *
 * Drip edge is ordered off the eave and again off the rake, starter and hip and
 * ridge the same way. That is how the quantity is arrived at, but it is not how
 * anyone wants to read the order: two lines of the same product with nothing
 * saying which is which is a thing to be queried at the counter.
 *
 * The sections are added up after each has been rounded, not before. Rounding
 * once at the end would occasionally buy a stick less than the roof needs.
 */
function mergeLines(lines: readonly GeneratedLine[]): GeneratedLine[] {
  const merged = new Map<string, GeneratedLine>();
  for (const line of lines) {
    // Keyed by supplier as well: the same product bought from two places is
    // two orders, and adding them together would put it on the wrong one.
    const key = `${line.supplierId ?? ""}:${line.product_id}`;
    const seen = merged.get(key);
    if (seen) {
      seen.qty += line.qty;
      // Both sums are kept. A merged drip edge line that only showed the eave
      // would look like an error to whoever checks it against the report.
      seen.calc = `${seen.calc} · ${line.calc}`;
    } else {
      merged.set(key, { ...line });
    }
  }
  return [...merged.values()].sort((a, b) => a.sort_order - b.sort_order);
}
